/**
 * Toast Context
 *
 * Provides toast notification functionality throughout the app.
 * Supports success, error, warning, and info variants.
 */

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Toast from '../components/Toast/Toast';

const ToastContext = createContext(undefined);

const DEFAULT_DURATION = 5000;
const MAX_TOASTS = 5;

let toastIdCounter = 0;

// Create a dedicated toast root element that's always at the end of body
const TOAST_ROOT_ID = 'ds-toast-root';
let toastRootElement = null;

const getOrCreateToastRoot = () => {
  if (!toastRootElement || !document.body.contains(toastRootElement)) {
    // Remove existing if it exists but is orphaned
    const existing = document.getElementById(TOAST_ROOT_ID);
    if (existing) {
      existing.remove();
    }
    
    // Create new root element - just a simple container for the portal
    toastRootElement = document.createElement('div');
    toastRootElement.id = TOAST_ROOT_ID;
    // Set z-index on the root element itself to ensure it's above everything
    toastRootElement.style.cssText = 'position: fixed; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 2147483647; overflow: visible;';
    document.body.appendChild(toastRootElement);
  }
  
  // Ensure it's always at the end of body (after any modals)
  // This is critical - DOM order matters for stacking contexts
  if (toastRootElement.parentNode === document.body) {
    // Move to end if there are any siblings after it
    const hasSiblingsAfter = toastRootElement.nextSibling !== null;
    if (hasSiblingsAfter) {
      document.body.appendChild(toastRootElement);
    }
  }
  
  return toastRootElement;
};

export const TOAST_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timeoutsRef = useRef({});

  const removeToast = useCallback((id) => {
    // Clear timeout if exists
    if (timeoutsRef.current[id]) {
      clearTimeout(timeoutsRef.current[id]);
      delete timeoutsRef.current[id];
    }

    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback((type, message, duration = DEFAULT_DURATION) => {
    const id = ++toastIdCounter;

    const newToast = {
      id,
      type,
      message,
      duration,
    };

    setToasts((prev) => {
      // Remove oldest if we've hit the max
      const updated = prev.length >= MAX_TOASTS ? prev.slice(1) : prev;
      return [...updated, newToast];
    });

    // Auto-dismiss after duration
    if (duration > 0) {
      timeoutsRef.current[id] = setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  }, [removeToast]);

  const success = useCallback((message, duration) => {
    return showToast(TOAST_TYPES.SUCCESS, message, duration);
  }, [showToast]);

  const error = useCallback((message, duration) => {
    return showToast(TOAST_TYPES.ERROR, message, duration);
  }, [showToast]);

  const warning = useCallback((message, duration) => {
    return showToast(TOAST_TYPES.WARNING, message, duration);
  }, [showToast]);

  const info = useCallback((message, duration) => {
    return showToast(TOAST_TYPES.INFO, message, duration);
  }, [showToast]);

  const clearAll = useCallback(() => {
    // Clear all timeouts
    Object.values(timeoutsRef.current).forEach(clearTimeout);
    timeoutsRef.current = {};
    setToasts([]);
  }, []);

  const value = {
    toasts,
    showToast,
    removeToast,
    success,
    error,
    warning,
    info,
    clearAll,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

/**
 * Toast Container Component
 * Renders all active toasts in a portal to ensure it's always on top
 */
const ToastContainer = ({ toasts, onRemove }) => {
  const [toastRoot, setToastRoot] = useState(null);

  useEffect(() => {
    // Get or create the toast root element
    const root = getOrCreateToastRoot();
    setToastRoot(root);

    // Ensure the root is always at the end of body after any DOM changes
    const ensureRootAtEnd = () => {
      if (root && root.parentNode === document.body) {
        // Use requestAnimationFrame to ensure this happens after any modal renders
        requestAnimationFrame(() => {
          if (root.parentNode === document.body && root.nextSibling !== null) {
            document.body.appendChild(root);
          }
        });
      }
    };

    // Watch for DOM changes to ensure toast root stays at the end
    const observer = new MutationObserver(ensureRootAtEnd);
    observer.observe(document.body, { childList: true, subtree: false });

    // Also ensure it's at the end on mount
    ensureRootAtEnd();

    return () => {
      observer.disconnect();
    };
  }, []);

  if (toasts.length === 0 || !toastRoot) return null;

  const toastContent = (
    <div className="ds-toast-container">
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          id={toast.id}
          type={toast.type}
          message={toast.message}
          onClose={() => onRemove(toast.id)}
        />
      ))}
    </div>
  );

  // Render in a portal to the dedicated toast root element
  // This ensures it's always at the end of body, after any modals
  return createPortal(toastContent, toastRoot);
};

/**
 * Hook to access toast context
 */
export const useToast = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export default ToastContext;
